/**
 * What the host dashboard flags, kept apart from the page so it can be tested without a canvas.
 *
 * The page's job is to answer "is anything on this server wrong?" without opening each organization,
 * so the rules lean towards the things only a host operator can see or fix: an organization that did
 * not start, a backup that failed or has gone quiet, a disk filling up, a build that rolled itself
 * back. An organization's own business - its overdue invoices, its urgent tickets - is shown on its
 * row and not raised here: that is what its own dashboard is for.
 */
import { TenantStatus, type HostDashboardDto, type HostServerDto, type HostTenantHealthDto } from './contracts.gen.js';
import { ago, plural } from './format.js';
import type { ChipTone } from './ui/Chip.js';

/** A scheduled backup with no success for longer than this has stopped working, whatever it says. */
export const STALE_BACKUP_DAYS = 8;
const DAY_MS = 86_400_000;

export interface HostAttention {
  readonly key: string;
  readonly tone: ChipTone;
  readonly tag: string;
  readonly title: string;
  readonly detail: string;
  /** The organization the item is about; null for the server itself. */
  readonly tenantId: number | null;
  /** Where inside that organization it is fixed; null when it is fixed from the host's own pages. */
  readonly tenantPath: string | null;
  /** The host page it is fixed on, when that is not the Organizations page. */
  readonly hostPath?: string;
}

export function diskShare(server: HostServerDto): number | null {
  const { diskFreeBytes: free, diskTotalBytes: total } = server;
  if (free == null || total == null || total <= 0) return null;
  return 100 - (free / total) * 100;
}

export function diskTone(usedPercent: number | null): ChipTone {
  if (usedPercent === null) return 'neutral';
  return usedPercent >= 90 ? 'danger' : usedPercent >= 80 ? 'warn' : 'ok';
}

/** "3 days 4 hours", for an uptime. Two units at most; a number that ticks draws the eye. */
export function duration(fromUtc: string, now = Date.now()): string {
  const minutes = Math.max(0, Math.floor((now - Date.parse(fromUtc)) / 60_000));
  if (minutes < 1) return 'less than a minute';
  if (minutes < 60) return plural(minutes, 'minute');
  const hours = Math.floor(minutes / 60);
  if (hours < 24) return `${plural(hours, 'hour')}${minutes % 60 ? ' ' + plural(minutes % 60, 'minute') : ''}`;
  const days = Math.floor(hours / 24);
  return `${plural(days, 'day')}${hours % 24 ? ' ' + plural(hours % 24, 'hour') : ''}`;
}

/** The parts of an organization's backup state the one-line summary reads. */
export interface BackupState {
  readonly running: boolean;
  readonly scheduled: boolean;
  readonly lastRunUtc: string | null;
  /** Null when no backup has run. */
  readonly lastRunSucceeded: boolean | null;
  readonly lastSuccessUtc: string | null;
}

/** The one-line state of an organization's backups. */
export function backupLine(t: HostTenantHealthDto, now = Date.now()): { text: string; tone: ChipTone } {
  return backupStateLine(
    {
      running: t.running,
      scheduled: t.backupsScheduled,
      lastRunUtc: t.lastBackupUtc,
      lastRunSucceeded: t.lastBackupSucceeded,
      lastSuccessUtc: t.lastSuccessfulBackupUtc
    },
    now
  );
}

export function backupStateLine(t: BackupState, now = Date.now()): { text: string; tone: ChipTone } {
  if (!t.running) return { text: 'Not running', tone: 'neutral' };
  if (t.lastRunSucceeded === false) return { text: `Last backup failed ${ago(t.lastRunUtc)}`, tone: 'danger' };
  if (!t.scheduled) {
    return t.lastSuccessUtc
      ? { text: `Not scheduled · last ${ago(t.lastSuccessUtc)}`, tone: 'warn' }
      : { text: 'Not scheduled', tone: 'warn' };
  }
  if (!t.lastSuccessUtc) return { text: 'Scheduled · none yet', tone: 'warn' };
  const stale = now - Date.parse(t.lastSuccessUtc) > STALE_BACKUP_DAYS * DAY_MS;
  return { text: `Last backup ${ago(t.lastSuccessUtc)}`, tone: stale ? 'warn' : 'ok' };
}

export function hostAttention(d: HostDashboardDto, now = Date.now()): HostAttention[] {
  const items: HostAttention[] = [];
  const s = d.server;

  if (s.lastUpdateFailure) {
    items.push({ key: 'update-failure', tone: 'danger', tag: 'Server', title: 'A build failed to start and was rolled back', detail: s.lastUpdateFailure, tenantId: null, tenantPath: null });
  }
  const used = diskShare(s);
  if (used !== null && used >= 80) {
    items.push({
      key: 'disk',
      tone: diskTone(used),
      tag: 'Server',
      title: `The data drive is ${Math.round(used)}% full`,
      detail: 'Backups, uploads and every organization\'s database live on it.',
      tenantId: null,
      tenantPath: null
    });
  }
  if (s.updateAvailable && s.latestVersion) {
    items.push({ key: 'update', tone: 'info', tag: 'Server', title: `Version ${s.latestVersion} is available`, detail: `Running ${s.version}.`, tenantId: null, tenantPath: null });
  }

  for (const t of d.tenants) {
    if (!t.running && t.status === TenantStatus.Active) {
      items.push({ key: `start:${t.id}`, tone: 'danger', tag: t.name, title: 'Not running', detail: t.startError ?? 'It did not start.', tenantId: t.id, tenantPath: null });
      continue;
    }
    if (!t.running) continue;
    const backup = backupLine(t, now);
    if (backup.tone === 'danger') {
      items.push({ key: `backup:${t.id}`, tone: 'danger', tag: t.name, title: 'The last backup failed', detail: t.lastBackupError ?? '', tenantId: t.id, tenantPath: null, hostPath: `/host-backups?org=${t.id}` });
    } else if (backup.tone === 'warn') {
      items.push({
        key: `backup:${t.id}`,
        tone: 'warn',
        tag: t.name,
        title: t.backupsScheduled ? `No successful backup in ${STALE_BACKUP_DAYS} days` : 'Backups are not scheduled',
        detail: backup.text,
        tenantId: t.id,
        tenantPath: null,
        hostPath: `/host-backups?org=${t.id}`
      });
    }
    if ((t.lockedAccounts ?? 0) > 0) {
      items.push({ key: `locks:${t.id}`, tone: 'warn', tag: t.name, title: `${plural(t.lockedAccounts ?? 0, 'account')} locked out`, detail: 'Open its Security page to release them.', tenantId: t.id, tenantPath: '/security' });
    }
  }
  return items;
}

/** Sums over the running organizations; a suspended one has no figures to add. */
export function hostTotals(tenants: readonly HostTenantHealthDto[]): {
  organizations: number;
  running: number;
  suspended: number;
  customers: number;
  devices: number;
  online: number;
  pending: number;
  staff: number;
  openTickets: number;
} {
  const sum = (pick: (t: HostTenantHealthDto) => number | null) => tenants.reduce((n, t) => n + (pick(t) ?? 0), 0);
  return {
    organizations: tenants.length,
    running: tenants.filter(t => t.running).length,
    suspended: tenants.filter(t => t.status === TenantStatus.Suspended).length,
    customers: sum(t => t.customers),
    devices: sum(t => t.devices),
    online: sum(t => t.agentsOnline),
    pending: sum(t => t.pendingDevices),
    staff: sum(t => t.staffUsers),
    openTickets: sum(t => t.openTickets)
  };
}
